// /tokenization/leagues (C-7): who leads tokenized assets by network, by
// issuer or manager and by platform, across every class or within one. The
// math is league.ts; each board is fetched once per visit.

import { useMemo, useState } from "react";

import { VENUES } from "../App";
import { API_BASE } from "../api";
import { ASSET_CLASSES } from "../classes";
import { useAllBoards } from "../components/AllVenues";
import { ClassSwitch } from "../components/ClassSwitch";
import { LeagueTable, REGISTRY_CLASSES, useLeagueData } from "../components/CrossClass";
import { compactUsd, count, utcTime } from "../format";
import { issuerLeague, networkLeague, onlyClass, platformLeague, type League, type LeagueInput } from "../league";
import styles from "../components/AllVenues.module.css";

const ROWS_SHOWN = 10;

/** "" is every class but stablecoins (they would swamp the rest), "all" every class; otherwise a /v1/classes id. */
const FILTERS: ReadonlyArray<readonly [string, string]> = [
  ["", "All classes but stablecoins"],
  ["all", "All classes with stablecoins"],
  ...ASSET_CLASSES.map((c) => [c.apiId, c.label] as const),
];

function readFilter(): string {
  const value = new URLSearchParams(location.search).get("class") ?? "";
  return FILTERS.some(([id]) => id === value) ? value : "";
}

function LeaguePanel({ title, table, note }: { title: string; table: League | null; note?: string }) {
  const [all, setAll] = useState(false);
  const rows = table ? (all ? table.rows : table.rows.slice(0, ROWS_SHOWN)) : [];
  const notes = [note, ...(table?.notes ?? [])].filter(Boolean);
  return (
    <section aria-label={title} className={`panel ${styles.panel}`}>
      <header className="panel-head">
        <h2>{title}</h2>
        <span>{table ? `${count(table.rows.filter((r) => !r.unranked).length)} ranked` : "loading"}</span>
      </header>
      {!table ? (
        <p className="panel-empty">Reading the boards…</p>
      ) : table.rows.length === 0 ? (
        <p className="panel-empty">Nothing to rank for this class in the API.</p>
      ) : (
        <>
          <LeagueTable rows={rows} showChange={false} showFlow={false} />
          {table.rows.length > ROWS_SHOWN ? (
            <button className={styles.more} onClick={() => setAll(!all)} type="button">
              {all ? `Show the top ${ROWS_SHOWN}` : `Show all ${count(table.rows.length)}`}
            </button>
          ) : null}
        </>
      )}
      {notes.length ? <p className={styles.footnote}>{notes.join(" ")}</p> : null}
    </section>
  );
}

export function Leagues() {
  const [filter, setFilter] = useState(readFilter);
  const allClasses = filter === "" || filter === "all";
  const withStablecoins = filter === "all" || filter === "stablecoin";
  const { index, boards, stablecoins, error } = useLeagueData(withStablecoins);
  const venues = useAllBoards(VENUES, false);

  const input = useMemo<LeagueInput | null>(() => {
    if (!index || (withStablecoins && !stablecoins)) return null;
    const every = { index, boards, venues, stablecoins: withStablecoins ? stablecoins : null };
    return allClasses ? every : onlyClass(every, filter);
  }, [filter, allClasses, withStablecoins, index, boards, venues, stablecoins]);
  const tables = useMemo(() => (input ? { networks: networkLeague(input), issuers: issuerLeague(input), platforms: platformLeague(input) } : null), [input]);

  function choose(next: string): void {
    setFilter(next);
    const url = new URL(location.href);
    if (next) url.searchParams.set("class", next);
    else url.searchParams.delete("class");
    history.replaceState(null, "", url);
  }

  const headline = tables?.networks.headline ?? null;
  const withStocks = allClasses || filter === "stock";
  const label = FILTERS.find(([id]) => id === filter)![1];

  return (
    <div className="terminal">
      <p className="command-bar">
        <span aria-hidden className="cursor" />
        <span>
          LEAG &lt;RWA&gt; <em>LEAGUE TABLES</em> &lt;GO&gt;
        </span>
        <span>PANTHER/TERMINAL</span>
      </p>
      <main>
        <header className="screen-head">
          <div className="screen-title">
            <h1 className="sr-only">League tables</h1>
            <ClassSwitch current={null} />
          </div>
          <span className="head-links">
            <a className="head-link" href="/tokenization/methodology">
              How we count
            </a>
            <a className="head-link" href={`${API_BASE}/v1`} rel="noreferrer" target="_blank">
              API
            </a>
          </span>
        </header>

        <div className="venue-bar">
          <label className="venue-picker">
            <span className="venue-picker-label">Asset class</span>
            <select className="venue-select" onChange={(e) => choose(e.target.value)} value={filter}>
              {FILTERS.map(([id, text]) => (
                <option key={id} value={id}>
                  {text}
                </option>
              ))}
            </select>
          </label>
        </div>

        <p className="venue-meta">
          {error ? (
            <span className="error">Data unavailable right now: {error}</span>
          ) : index ? (
            <>
              {label}: <strong>{!tables ? "…" : headline === null ? "unavailable" : compactUsd(headline)}</strong>, each product counted once ·{" "}
              {allClasses
                ? `${count(boards.length)} of ${count(REGISTRY_CLASSES.length)} class boards · ${count(venues.filter((v) => v.board).length)} of ${count(venues.length)} stock venues · `
                : null}
              as of <strong>{utcTime(index.as_of)}</strong>
            </>
          ) : (
            "Loading…"
          )}
        </p>

        <LeaguePanel
          note="A product on several chains counts on each chain at the supply read there times its price. Value no chain can be given is listed apart, unranked."
          table={tables?.networks ?? null}
          title="Networks"
        />
        <LeaguePanel table={tables?.issuers ?? null} title="Issuers & managers" />
        <LeaguePanel note={withStocks ? "A stock venue is the platform for the tokens it lists." : undefined} table={tables?.platforms ?? null} title="Platforms" />
      </main>
      <footer className="footer">
        Panther Research · Share is of the total shown above. Data from issuer feeds, on-chain reads and DefiLlama, refreshed every 30 minutes. Not investment advice.
      </footer>
    </div>
  );
}
